import React, { useState, useEffect } from "react";
import { getEstadisticasService } from "../../services/EstadisticaService";
import { getTiposPagoService } from "../../services/EntradaService";
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
} from "recharts";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale } from 'react-datepicker';
import { es } from 'date-fns/locale/es';

registerLocale('es', es);

const EstadisticasForm = () => {
    const [fechaDesde, setFechaDesde] = useState(null);
    const [fechaHasta, setFechaHasta] = useState(null);
    const [modo, setModo] = useState("entradas");
    const [tipoPago, setTipoPago] = useState("");
    const [resultado, setResultado] = useState(null);
    const [tiposPago, setTiposPago] = useState([]);
    const [loading, setLoading] = useState(false);

    const dateFormat = (d) => {
        if (!d) return '';
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    useEffect(() => {
        const fetchTipos = async () => {
            try {
                const tipos = await getTiposPagoService();
                setTiposPago(tipos || []);
            } catch (e) {
                console.error("Error cargando tipos de pago", e);
            }
        };
        fetchTipos();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setResultado(null);
        setLoading(true);
        try {
            const filtro = { fechaDesde: dateFormat(fechaDesde), fechaHasta: dateFormat(fechaHasta), modo, tipoPago: tipoPago || null };
            const data = await getEstadisticasService(filtro);
            const personasPorHora = Array.isArray(data.entradas) ? data.entradas : [];
            setResultado({ ...data, personasPorHora });
        } catch (err) {
            const msg = err?.mensaje || err?.message || "Error al obtener estadísticas";
            alert(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Estadísticas</h2>

            <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5 bg-white border border-gray-200 rounded-lg shadow-sm mb-6">
                <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">Desde:</label>
                    <DatePicker selected={fechaDesde} onChange={(d) => setFechaDesde(d)} locale="es" dateFormat="dd/MM/yyyy" placeholderText="Desde"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                </div>
                <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">Hasta:</label>
                    <DatePicker selected={fechaHasta} onChange={(d) => setFechaHasta(d)} locale="es" dateFormat="dd/MM/yyyy" placeholderText="Hasta"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                </div>
                <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">Modo:</label>
                    <select value={modo} onChange={(e) => setModo(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                        <option value="entradas">Solo Entradas</option>
                        <option value="eventos">Solo Eventos</option>
                        <option value="ambos">Entradas + Eventos</option>
                    </select>
                </div>
                <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">Tipo de Pago:</label>
                    <select value={tipoPago} onChange={(e) => setTipoPago(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                        <option value="">Todos</option>
                        {tiposPago.map((t, i) => {
                            const val = typeof t === "string" ? t : t.nombre || t.id;
                            const label = typeof t === "string" ? t : t.nombre || t.id;
                            return <option key={i} value={val}>{label}</option>;
                        })}
                    </select>
                </div>
                <div className="sm:col-span-2 text-center">
                    <button type="submit" disabled={loading}
                        className="px-6 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                        {loading ? "Cargando..." : "Consultar"}
                    </button>
                </div>
            </form>

            {resultado && (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5 text-center">
                            <p className="text-sm text-gray-500 mb-1">Ingresos Totales</p>
                            <p className="text-2xl font-semibold text-gray-900">${resultado.totalIngresos}</p>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5 text-center">
                            <p className="text-sm text-gray-500 mb-1">Cantidad Personas</p>
                            <p className="text-2xl font-semibold text-gray-900">{resultado.totalPersonas}</p>
                        </div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5">
                        <h4 className="text-sm font-semibold text-gray-700 text-center mb-4">Personas por hora</h4>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={resultado.personasPorHora}>
                                <XAxis dataKey="hora" tick={{ fontSize: 12 }} />
                                <YAxis tick={{ fontSize: 12 }} />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="cantidad" fill="#3b82f6" name="Personas" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EstadisticasForm;
